import React from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { useSelector, shallowEqual } from 'react-redux';
import Icon from '../Icon/Icon';
import './CommunityFilter.css';
import { ALL_COMMUNITIES_CHOICE } from '../../utils/community/communityConstants';
import CommunityContextShowMoreButton from '../CommunityContext/CommunityContextShowMoreButton/CommunityContextShowMoreButton';
import CommunityContextFloatingMenu from '../CommunityContext/CommunityContextFloatingMenu/CommunityContextFloatingMenu';
import { getCommunityFilter } from '../../store/selectors/community/communitySelectors';

const CommunityFilter = ({
  isVisibleCommunityMenu,
  communityFilter,
  isFetchingComments,
  setCommunityFilter,
  toggleShowCommunityMenu,
  communities
}) => {
  const selectedCommunityFilter = useSelector(getCommunityFilter, shallowEqual);

  if (isFetchingComments) return null;

  const _showCommunityContextSwitchMenu = (e) => {
    e.stopPropagation();
    toggleShowCommunityMenu();
  };

  const updateCommunityFilter = (data) => {
    if (data !== communityFilter) {
      setCommunityFilter(data);
    }
  };

  const setupMenuItemIcon = (shouldRenderIcon) => {
    return shouldRenderIcon ? (
      <Icon style={{ float: 'right' }} icon={['fal', 'chevron-down']} />
    ) : null;
  };

  const _generateCommunityMenuItems = () => {
    // Setup our communities as menu items
    let items = Object.keys(communities).map((communityKey) => {
      const isSelected =
        selectedCommunityFilter.name === communities[communityKey].name;
      return {
        label: communities[communityKey].name,
        onClick: () => updateCommunityFilter(communities[communityKey]),
        selected: isSelected,
        children: setupMenuItemIcon(isSelected)
      };
    });

    // Add the ALL_COMMUNITIES_CHOICE to the menu item list
    const isSelected =
      selectedCommunityFilter.name === ALL_COMMUNITIES_CHOICE.name;
    items = [
      {
        children: setupMenuItemIcon(isSelected),
        label: i18next.t('all'),
        onClick: () => updateCommunityFilter(ALL_COMMUNITIES_CHOICE),
        selected: isSelected
      },
      ...items
    ];
    // Sort the array, first by its 'selected' status then alphabetically
    items = items.sort((a, b) => {
      return b.selected - a.selected || a.label.localeCompare(b.label);
    });
    return items;
  };

  return (
    <div styleName="community-container">
      <Icon size={{ height: '1.4em' }} color="#4F4F4F" icon={['fa', 'users']} />
      <p styleName="community-text">
        {i18next.t('viewCommentsByCommunity')}
        <span styleName="buttons-wrapper">
          {!isVisibleCommunityMenu && (
            <CommunityContextShowMoreButton
              onClick={_showCommunityContextSwitchMenu}
              text={i18next.t(communityFilter.name, communityFilter.name)}
              icon={['fal', 'chevron-down']}
            />
          )}
          {isVisibleCommunityMenu && (
            <CommunityContextFloatingMenu
              menuItems={_generateCommunityMenuItems()}
            />
          )}
        </span>
      </p>
    </div>
  );
};

CommunityFilter.propTypes = {
  toggleShowCommunityMenu: PropTypes.func.isRequired,
  isFetchingComments: PropTypes.bool.isRequired,
  isVisibleCommunityMenu: PropTypes.bool,
  setCommunityFilter: PropTypes.func.isRequired,
  communityFilter: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  communities: PropTypes.objectOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  )
};

CommunityFilter.defaultProps = {
  isVisibleCommunityMenu: false,
  communities: {}
};

export default CommunityFilter;
